import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useSession } from 'session-context';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import WeatherWindGenerator from 'weather-wind-generator';
import NavalEncounterGenerator from 'naval-encounter-generator';
import RulesReference from './rules-reference-component';
import DMFleetManager from './dm-fleet-manager';
import PlayerFleetManager from './player-fleet-manager';
import { AnchorIcon, Book, Ship } from 'lucide-react';
import { VoyageSetup } from './components/voyage-setup';
import { VoyagePanel } from './components/voyage-panel';
import { DeadReckoningLog } from './components/dead-reckoning-log';
import { defaultNavigationState } from './types/navigation-types';

const CampaignDashboard: React.FC = () => {
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const {
    sessionId: contextSessionId,
    gameState,
    updateGameState,
    isDM,
    joinSession
  } = useSession();

  // Track whether this dashboard has ever held a session. Once it has, we must
  // not auto-join again: leaving a session clears contextSessionId (an urgent
  // update) before React Router commits the navigation to '/' (a deferred
  // transition), leaving a render where the route is still /session/:id but the
  // context is null. Without this guard the effect below would immediately
  // re-join and bounce the user back into the session they just left.
  const hasHadSession = React.useRef(false);
  React.useEffect(() => {
    if (contextSessionId) hasHadSession.current = true;
  }, [contextSessionId]);

  // Auto-join session on a genuine URL visit / reload (URL has a sessionId but
  // the context never had one yet).
  React.useEffect(() => {
    if (urlSessionId && !contextSessionId && !hasHadSession.current) {
      joinSession(urlSessionId, false);
    }
  }, [urlSessionId, contextSessionId, joinSession]);

  // Check if we have a valid session URL
  if (!urlSessionId) {
    return <Navigate to="/" replace />;
  }

  // Check if we have game state
  if (!gameState) {
    return <div>Loading session...</div>;
  }

  // Check if user is DM
  const userIsDM = isDM();

  const handleWeatherGenerated = (weatherData: typeof gameState.weatherData) => {
    updateGameState('weatherData', weatherData);
  };

  return (
    <div className="container mx-auto p-4">
      <Tabs defaultValue="voyage" className="w-full">
        <TabsList className="grid grid-cols-3 mb-6">
          <TabsTrigger value="voyage" className="flex items-center gap-2">
            <Ship className="h-4 w-4" />
            Voyage
          </TabsTrigger>
          <TabsTrigger value="combat" className="flex items-center gap-2">
            <AnchorIcon className="h-4 w-4" />
            Ship Combat
          </TabsTrigger>
          <TabsTrigger value="rules" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            Rules Reference
          </TabsTrigger>
        </TabsList>

        {/* Voyage Tab */}
        <TabsContent value="voyage" className="space-y-6">
          {!gameState.voyageActive ? (
            <VoyageSetup />
          ) : (
            <Tabs defaultValue="voyage" className="w-full">
              <TabsList className={`grid ${userIsDM ? 'grid-cols-2' : 'grid-cols-1'} mb-6`}>
                <TabsTrigger value="voyage">Voyage</TabsTrigger>
                {userIsDM && <TabsTrigger value="dm">DM Controls</TabsTrigger>}
              </TabsList>

              <TabsContent value="voyage" className="space-y-6">
                <VoyagePanel />
              </TabsContent>

              {userIsDM && (
                <TabsContent value="dm" className="space-y-6">
                  <Tabs defaultValue="weather" className="w-full">
                    <TabsList className="grid grid-cols-3 mb-6">
                      <TabsTrigger value="weather">Weather</TabsTrigger>
                      <TabsTrigger value="encounters">Naval Encounters</TabsTrigger>
                      <TabsTrigger value="navigation">Navigation</TabsTrigger>
                    </TabsList>

                    <TabsContent value="weather" className="space-y-6">
                      <WeatherWindGenerator
                        onWeatherGenerated={handleWeatherGenerated}
                        currentWeather={gameState.weatherData}
                      />
                    </TabsContent>

                    <TabsContent value="encounters" className="space-y-6">
                      <NavalEncounterGenerator
                        weatherData={gameState.weatherData}
                        voyageDay={gameState.voyageDay ?? 0}
                        crewSize={gameState.crewSize ?? 0}
                        provisions={gameState.provisions ?? 0}
                        navStatus={(gameState.navigation ?? defaultNavigationState).status}
                        destinationName={gameState.voyageDestination?.name}
                      />
                    </TabsContent>

                    <TabsContent value="navigation" className="space-y-6">
                      <DeadReckoningLog
                        navigation={gameState.navigation ?? defaultNavigationState}
                        navLog={gameState.navLog ?? []}
                      />
                    </TabsContent>
                  </Tabs>
                </TabsContent>
              )}
            </Tabs>
          )}
        </TabsContent>

        {/* Ship Combat Tab */}
        <TabsContent value="combat">
          <Tabs defaultValue="player" className="w-full">
            <TabsList className="grid grid-cols-2 mb-6">
              <TabsTrigger value="player">Player Vessels</TabsTrigger>
              {userIsDM && <TabsTrigger value="dm">DM Vessels</TabsTrigger>}
            </TabsList>

            {/* Player Vessels Section */}
            <TabsContent value="player">
              <PlayerFleetManager />
            </TabsContent>

            {/* DM Vessels Section */}
            {userIsDM && (
              <TabsContent value="dm">
                <DMFleetManager />
              </TabsContent>
            )}
          </Tabs>
        </TabsContent>

        {/* Rules Reference Tab */}
        <TabsContent value="rules">
          <RulesReference />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CampaignDashboard;